<script setup lang="ts">
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '../stores/auth'
import { useToastStore } from '../stores/toast'
import categoryService from '../services/category.service'
import coachService from '../services/coach.service'
import playerService from '../services/player.service'
import eventService from '../services/event.service'
import ParticipantSelectorV2 from './ParticipantSelectorV2.vue'

const props = defineProps<{
  visible: boolean
}>()

const emit = defineEmits<{
  (e: 'close'): void
}>()

const { t } = useI18n()
const authStore = useAuthStore()
const toastStore = useToastStore()

const clubUuid = () => authStore.initData?.club?.uuid || ''

const categories = ref<any[]>([])
const coaches = ref<any[]>([])
const players = ref<any[]>([])
const loadingPlayers = ref(false)
const loadingCoaches = ref(false)

const dateFrom = ref<string>('')
const dateTo = ref<string>('')
const selectedPlayers = ref<string[]>([])
const selectedCoaches = ref<string[]>([])
const submitting = ref(false)

async function loadCategories() {
  try {
    const res = await categoryService.getCategories(clubUuid(), 1, 9999)
    categories.value = res.data?.categories ?? res.data ?? []
  } catch {
    categories.value = []
  }
}

async function loadCoaches() {
  loadingCoaches.value = true
  try {
    const res = await coachService.getCoaches(clubUuid(), 9999)
    coaches.value = res.data || []
  } catch {
    coaches.value = []
  } finally {
    loadingCoaches.value = false
  }
}

async function loadPlayers() {
  loadingPlayers.value = true
  try {
    const res = await playerService.getPlayers(clubUuid(), { per_page: 9999 })
    players.value = res.data?.players || res.data || []
  } catch {
    players.value = []
  } finally {
    loadingPlayers.value = false
  }
}

watch(() => props.visible, async (val) => {
  if (val) {
    dateFrom.value = ''
    dateTo.value = ''
    selectedPlayers.value = []
    selectedCoaches.value = []
    await Promise.all([loadCategories(), loadCoaches(), loadPlayers()])
  }
})

async function submit() {
  if (!dateFrom.value || !dateTo.value) return
  submitting.value = true
  try {
    await eventService.requestAttendanceReport(clubUuid(), {
      date_from: dateFrom.value,
      date_to: dateTo.value,
      player_uuids: selectedPlayers.value,
      coach_uuids: selectedCoaches.value,
    })
    toastStore.success(t('eventCycles.attendanceReport.successMessage'))
    emit('close')
  } catch (err: any) {
    const msg = err?.response?.data?.message || t('eventCycles.attendanceReport.errorMessage')
    toastStore.error(msg)
  } finally {
    submitting.value = false
  }
}
</script>

<template>
  <Teleport to="body">
    <div
      v-if="visible"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      @click.self="emit('close')"
    >
      <div class="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <!-- Header -->
        <div class="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 class="text-xl font-bold text-gray-800">
            {{ t('eventCycles.attendanceReport.modalTitle') }}
          </h2>
          <button
            type="button"
            class="text-gray-500 hover:text-gray-700"
            @click="emit('close')"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <!-- Body -->
        <div class="p-6 space-y-5">
          <!-- Date range -->
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                {{ t('eventCycles.attendanceReport.dateFrom') }}
              </label>
              <input
                v-model="dateFrom"
                type="date"
                class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                {{ t('eventCycles.attendanceReport.dateTo') }}
              </label>
              <input
                v-model="dateTo"
                type="date"
                :min="dateFrom"
                class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <!-- Participants selector -->
          <div>
            <p class="text-sm font-medium text-gray-700 mb-2">
              {{ t('eventCycles.attendanceReport.participants') }}
            </p>
            <ParticipantSelectorV2
              v-model:selectedPlayers="selectedPlayers"
              v-model:selectedCoaches="selectedCoaches"
              :categories="categories"
              :players="players"
              :coaches="coaches"
              :loadingPlayers="loadingPlayers"
              :loadingCoaches="loadingCoaches"
            />
          </div>
        </div>

        <!-- Footer -->
        <div class="flex justify-end gap-3 px-6 pb-6">
          <button
            type="button"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
            @click="emit('close')"
          >
            {{ t('eventCycles.attendanceReport.cancel') }}
          </button>
          <button
            type="button"
            :disabled="submitting || !dateFrom || !dateTo"
            class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            @click="submit"
          >
            {{ submitting ? t('eventCycles.attendanceReport.submitting') : t('eventCycles.attendanceReport.submit') }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>
